(() => {
  const componentName = 'vl-responsive-card-grid-slider';
  const mobileQuery = '(max-width: 767px)';

  if (customElements.get(componentName)) return;

  class VLResponsiveCardGridSlider extends HTMLElement {
    constructor() {
      super();

      this.activeIndex = 0;
      this.scrollFrame = null;
      this.isInitialized = false;
      this.resizeObserver = null;
      this.mediaQuery = window.matchMedia(mobileQuery);

      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.updateFromPosition = this.updateFromPosition.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.track = this.querySelector('[data-vl-responsive-card-grid-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-vl-responsive-card-grid-slider-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-vl-responsive-card-grid-slider-dot]'));
      this.previousButton = this.querySelector('[data-vl-responsive-card-grid-slider-previous]');
      this.nextButton = this.querySelector('[data-vl-responsive-card-grid-slider-next]');

      if (!this.track || !this.slides.length) return;

      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('keydown', this.handleKeydown);
      this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick));
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.mediaQuery.addEventListener('change', this.handleViewportChange);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.handleViewportChange);
        this.resizeObserver.observe(this.track);
      }

      this.isInitialized = true;
      this.handleViewportChange();
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.track?.removeEventListener('scroll', this.handleScroll);
      this.track?.removeEventListener('keydown', this.handleKeydown);
      this.dots.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.mediaQuery.removeEventListener('change', this.handleViewportChange);
      this.resizeObserver?.disconnect();

      if (this.scrollFrame !== null) {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = null;
      }

      this.isInitialized = false;
    }

    handlePrevious() {
      this.goTo(this.activeIndex - 1);
    }

    handleNext() {
      this.goTo(this.activeIndex + 1);
    }

    handleDotClick(event) {
      this.goTo(Number(event.currentTarget.dataset.vlResponsiveCardGridSliderIndex));
    }

    handleKeydown(event) {
      if (!this.mediaQuery.matches) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handlePrevious();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleNext();
      }
    }

    handleScroll() {
      if (!this.mediaQuery.matches) return;
      if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(this.updateFromPosition);
    }

    handleBlockSelect(event) {
      if (!this.mediaQuery.matches) return;

      const selectedSlide = event.target.closest('[data-vl-responsive-card-grid-slider-slide]');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      this.goTo(Number(selectedSlide.dataset.vlResponsiveCardGridSliderIndex), false);
    }

    handleViewportChange() {
      if (!this.track) return;

      if (!this.mediaQuery.matches) {
        this.activeIndex = 0;
        this.track.scrollTo({ left: 0, behavior: 'auto' });
        this.updateUI();
        return;
      }

      this.activeIndex = Math.min(this.activeIndex, this.getMaxIndex());
      this.goTo(this.activeIndex, false);
    }

    updateFromPosition() {
      this.scrollFrame = null;
      if (!this.mediaQuery.matches) return;

      const trackLeft = this.track.getBoundingClientRect().left + this.getTrackInset();
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (nearestIndex === this.activeIndex) {
        this.updateUI();
        return;
      }

      this.activeIndex = nearestIndex;
      this.updateUI();
    }

    goTo(index, smooth = true) {
      const nextIndex = Math.max(0, Math.min(index, this.getMaxIndex()));
      const targetSlide = this.slides[nextIndex];
      if (!targetSlide) return;

      this.activeIndex = nextIndex;

      if (this.mediaQuery.matches) {
        this.track.scrollTo({
          left: this.getScrollTarget(targetSlide),
          behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
        });
      }

      this.updateUI();
    }

    getMaxIndex() {
      return this.mediaQuery.matches ? Math.max(0, this.slides.length - 1) : 0;
    }

    getTrackInset() {
      return Number.parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    }

    getScrollTarget(targetSlide) {
      return (
        this.track.scrollLeft +
        targetSlide.getBoundingClientRect().left -
        this.track.getBoundingClientRect().left -
        this.getTrackInset()
      );
    }

    updateUI() {
      const maxIndex = this.getMaxIndex();
      if (this.activeIndex > maxIndex) this.activeIndex = maxIndex;

      this.classList.toggle('is-static', maxIndex === 0);

      this.dots.forEach((dot, index) => {
        const isActive = index === this.activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
        dot.tabIndex = isActive ? 0 : -1;
      });

      if (this.previousButton) this.previousButton.disabled = this.activeIndex <= 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex >= maxIndex;
    }
  }

  customElements.define(componentName, VLResponsiveCardGridSlider);
})();
